import { useState } from 'react';
import { Alert, Group, Paper, SegmentedControl, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { BarChart, ChartTooltip } from '@mantine/charts';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { ProviderCoverage } from '../types';
import { formatAbbreviatedCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

type Metric = 'aum' | 'products';
type View = 'matched' | 'unmatched';

type ProviderChartProps = {
  providers: ProviderCoverage[];
};

const SERIES_LABELS: Record<View, Record<Metric, string>> = {
  matched: { aum: 'Connected AUM', products: 'Connected products' },
  unmatched: { aum: 'Unmatched AUM', products: 'Unmatched products' },
};

const DATA_KEYS: Record<View, Record<Metric, keyof ProviderCoverage>> = {
  matched: { aum: 'matchedAum', products: 'matchedCount' },
  unmatched: { aum: 'unmatchedAum', products: 'unmatchedCount' },
};

function getChartData(
  providers: ProviderCoverage[],
  metric: Metric,
  view: View,
  seriesLabel: string,
) {
  const key = DATA_KEYS[view][metric];

  return [...providers]
    .sort((a, b) => a.provider.localeCompare(b.provider))
    .map(p => ({ provider: p.provider, [seriesLabel]: p[key] }));
}

function ChartTitle() {
  return (
    <Text size="sm" fw={FONT_WEIGHTS.semibold}>
      Coverage by provider
    </Text>
  );
}

function EmptyState() {
  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Stack gap="sm">
        <ChartTitle />
        <Alert icon={<IconInfoCircle />} color="yellow" variant="light">
          <Text size="sm">
            No provider data available yet. Provider feeds will appear here once configured.
          </Text>
        </Alert>
      </Stack>
    </Paper>
  );
}

function ChartControls({
  view,
  onViewChange,
  metric,
  onMetricChange,
  showViewToggle,
}: {
  view: View;
  onViewChange: (v: View) => void;
  metric: Metric;
  onMetricChange: (m: Metric) => void;
  showViewToggle: boolean;
}) {
  return (
    <Group gap="xs">
      {showViewToggle && (
        <SegmentedControl
          size="xs"
          value={view}
          onChange={v => onViewChange(v as View)}
          data={[
            { label: 'Unmatched', value: 'unmatched' },
            { label: 'Connected', value: 'matched' },
          ]}
        />
      )}
      <SegmentedControl
        size="xs"
        value={metric}
        onChange={v => onMetricChange(v as Metric)}
        data={[
          { label: 'AUM', value: 'aum' },
          { label: 'Products', value: 'products' },
        ]}
      />
    </Group>
  );
}

export default function ProviderChart({ providers }: ProviderChartProps) {
  const currency = useCurrency();
  const [metric, setMetric] = useState<Metric>('aum');
  const [view, setView] = useState<View>('unmatched');

  if (providers.length === 0) return <EmptyState />;

  const hasNoGaps = providers.every(p => p.unmatchedCount === 0);
  const effectiveView = hasNoGaps ? 'matched' : view;
  const seriesLabel = SERIES_LABELS[effectiveView][metric];
  const data = getChartData(providers, metric, effectiveView, seriesLabel);

  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <ChartTitle />
          <ChartControls
            view={view}
            onViewChange={setView}
            metric={metric}
            onMetricChange={setMetric}
            showViewToggle={!hasNoGaps}
          />
        </Group>
        <BarChart
          h={300}
          data={data}
          dataKey="provider"
          series={[{ name: seriesLabel, color: 'brand.6' }]}
          valueFormatter={
            metric === 'aum' ? value => formatAbbreviatedCurrency(value, currency) : String
          }
          gridAxis="x"
          tickLine="none"
          xAxisProps={{ angle: -30, textAnchor: 'end', height: 60 }}
          minBarSize={3}
          withBarValueLabel
          valueLabelProps={{ fontWeight: FONT_WEIGHTS.bold, fontSize: '14px' }}
          withTooltip
          tooltipProps={{
            content: ({ payload, label }) => (
              <ChartTooltip label={label} payload={[...(payload ?? [])]} showColor={false} />
            ),
          }}
          gridProps={{ yAxisId: 'left' }}
        />
      </Stack>
    </Paper>
  );
}
